import React from 'react';
import { Avatar, Badge, Skeleton } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPen } from '@fortawesome/free-solid-svg-icons';
import { getMediaURL } from '../../context/API';
import {
  ProfileCard,
  ProfileHeader,
  ProfileInfo,
  ProfileDetails,
  ProfileName,
  ProfileUsername,
  ProfileLocation,
  EditProfileButton,
} from './Styles';
import type { ProfileSectionProps } from './types';

const ProfileSection: React.FC<ProfileSectionProps> = ({
  user,
  role,
  editingSection,
  loading,
  profileLoading,
  onEdit,
}) => {
  // Get logo URL - check user.logo first, then user.merchant?.logo
  const getLogoUrl = () => {
    if (!user) return null;
    
    const userAny = user as any;
    const logo = userAny.logo || userAny.merchant?.logo;
    
    if (!logo || logo === null || logo === 'null' || logo === '{}' || logo === '%7B%7D') {
      return null;
    }
    
    const logoString = typeof logo === 'string' ? logo.trim() : String(logo);
    if (logoString === '' || logoString === 'null' || logoString === '{}') {
      return null;
    }
    
    // If logo is already a full URL, return it directly
    if (logoString.startsWith('http://') || logoString.startsWith('https://')) {
      return logoString;
    }
    
    // Use getMediaURL to format the logo path
    return getMediaURL(logoString);
  };

  const logoUrl = getLogoUrl();
  const [imageError, setImageError] = React.useState(false);
  
  const avatarSrc = logoUrl && !imageError
    ? logoUrl 
    : user 
      ? `https://ui-avatars.com/api/?name=${user.name || user.username}&background=0D9488&color=fff&size=128`
      : undefined;
  
  return (
    <ProfileCard>
      <ProfileHeader>
        <ProfileInfo>
          <Avatar
            size={80}
            src={avatarSrc}
            icon={<UserOutlined />}
            onError={() => {
              setImageError(true);
              return false;
            }}
          />
          <ProfileDetails>
            {profileLoading ? (
              <>
                <Skeleton.Input active size="small" style={{ width: 150, height: 24, marginBottom: 8 }} />
                <Skeleton.Input active size="small" style={{ width: 120, height: 20, marginBottom: 8 }} />
                <Skeleton.Input active size="small" style={{ width: 200, height: 20 }} />
              </>
            ) : user ? (
              <>
                <ProfileName level={3}>{user.name || user.username}</ProfileName>
                <ProfileUsername>@{user.username}</ProfileUsername>
                <ProfileLocation>
                  <FontAwesomeIcon icon={faMapMarkerAlt}/>
                  {user.address || 'Location not provided'}
                </ProfileLocation>
                <Badge
                  color={role === 'merchant' ? 'blue' : 'purple'}
                  text={role === 'merchant' ? 'Merchant' : 'Administrator'}
                  style={{ marginTop: 8 }}
                />
              </>
            ) : null}
          </ProfileDetails>
        </ProfileInfo>
        <EditProfileButton
          icon={<FontAwesomeIcon icon={faPen} style={{ fontSize: 14 }} />}
          onClick={onEdit}
          disabled={loading || profileLoading}
        >
          {editingSection === 'profile' ? 'Editing...' : 'Edit Profile'}
        </EditProfileButton>
      </ProfileHeader>
    </ProfileCard>
  );
};

export default React.memo(ProfileSection);

